import { minutesToMilliseconds } from 'date-fns';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  retrieveOrganizationCredits,
  retrieveOrganizationCreditTransactions,
  addOrganizationCredits,
  chargeOrganization,
  listBillingServices,
  listPrices,
  listMultipliers,
  ListPricesParams,
  ListMultipliersParams,
} from 'services/backend/billing';
import { ReactQueryFunctionCallbacks } from 'types/internal';

export const billingQueries = {
  all: () => ['credit'],
  organizationCredits: (opts: { organizationId: number }) =>
    queryOptions({
      queryFn: () =>
        retrieveOrganizationCredits({ orgId: opts.organizationId }),
      queryKey: [...billingQueries.all(), 'organization', opts.organizationId],
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
    }),
  organizationCreditTransactions: (opts: {
    organizationId: number;
    page?: number;
    limit?: number;
  }) =>
    queryOptions({
      queryFn: () =>
        retrieveOrganizationCreditTransactions({
          orgId: opts.organizationId,
          page: opts.page,
          limit: opts.limit,
        }),
      queryKey: [
        ...billingQueries.all(),
        'organization',
        opts.organizationId,
        'transactions',
        opts.page,
        opts.limit,
      ],
    }),
};

export function useOrganizationCredits({
  organizationId,
}: {
  organizationId: number | undefined;
}) {
  const { data: orgCredits, isLoading: isLoadingCredits } = useQuery({
    ...billingQueries.organizationCredits({
      organizationId: organizationId ?? 0,
    }),
    enabled: organizationId !== undefined,
  });
  return { isLoadingCredits, orgCredits };
}

export function useOrganizationCreditTransactions({
  organizationId,
  page,
  limit,
}: {
  organizationId: number | undefined;
  page?: number;
  limit?: number;
}) {
  const {
    data: creditTransactions,
    isLoading: isLoadingCreditTransactions,
    isError: isErrorCreditTransactions,
    error: creditTransactionsError,
  } = useQuery({
    ...billingQueries.organizationCreditTransactions({
      organizationId: organizationId ?? 0,
      page,
      limit,
    }),
    enabled: organizationId !== undefined,
  });
  return {
    isLoadingCreditTransactions,
    creditTransactions,
    isErrorCreditTransactions,
    creditTransactionsError,
  };
}

export function useAddOrganizationCredits(
  opts?: ReactQueryFunctionCallbacks<typeof addOrganizationCredits>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: addOrganizationCredits,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: billingQueries.organizationCredits({
          organizationId: args[1].organizationId,
        }).queryKey,
      });

      opts?.onSuccess?.(...args);
    },
  });
}

export function useChargeOrganization(
  opts?: ReactQueryFunctionCallbacks<typeof chargeOrganization>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: chargeOrganization,
    onSuccess: (...args) => {
      const { organizationId } = args[1];
      queryClient.invalidateQueries({
        queryKey: billingQueries.organizationCredits({ organizationId })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: billingQueries.organizationCreditTransactions({
          organizationId,
        }).queryKey,
      });

      opts?.onSuccess?.(...args);
    },
  });
}

export const billingServicesQueries = {
  all: () => ['billingServices'],
  list: () =>
    queryOptions({
      queryFn: () => listBillingServices(),
      queryKey: billingServicesQueries.all(),
      staleTime: minutesToMilliseconds(30),
    }),
};

export function useBillingServices(opts?: { enabled?: boolean }) {
  return useQuery({ ...billingServicesQueries.list(), enabled: opts?.enabled });
}

export const billingPricesQueries = {
  all: () => ['billingPrices'],
  list: (params?: ListPricesParams) =>
    queryOptions({
      queryFn: () => listPrices(params),
      queryKey: [...billingPricesQueries.all(), params],
      staleTime: minutesToMilliseconds(30),
    }),
};

export function useBillingPrices(
  params?: ListPricesParams,
  opts?: { enabled?: boolean },
) {
  return useQuery({
    ...billingPricesQueries.list(params),
    enabled: opts?.enabled,
  });
}

export const billingMultipliersQueries = {
  all: () => ['billingMultipliers'],
  list: (params?: ListMultipliersParams) =>
    queryOptions({
      queryFn: () => listMultipliers(params),
      queryKey: [...billingMultipliersQueries.all(), params],
      staleTime: minutesToMilliseconds(30),
    }),
};

export function useBillingMultipliers(
  params?: ListMultipliersParams,
  opts?: { enabled?: boolean },
) {
  return useQuery({
    ...billingMultipliersQueries.list(params),
    enabled: opts?.enabled,
  });
}
